import { computed, createCommentVNode, defineComponent, h, type PropType } from 'vue'
import type { MetaDataType } from '@/models/MetaData'
import Config from '@/utils/config'

export const MetaTags = defineComponent({
  name: 'MetaTags',
  props: {
    metaDataFn: {
      type: Function as PropType<() => MetaDataType>,
      required: true,
    },
    ssr: { type: Boolean, default: false },
  },
  setup(props) {
    const removeLanguageSegment = (url: string): string => {
      const urlObject = new URL(url)
      const path = urlObject.pathname
      const segments = path.split('/')
      const languages = Config.languages
      if (segments.length > 1 && languages.includes(segments[1])) {
        segments.splice(1, 1)
      }
      urlObject.pathname = segments.join('/')
      let newUrl = urlObject.toString()
      if (newUrl.endsWith('/')) {
        newUrl = newUrl.slice(0, -1)
      }
      return newUrl
    }

    const setLanguageSegment = (url: string, language: string): string => {
      const urlObject = new URL(url)
      const path = urlObject.pathname
      const segments = path.split('/')
      const languages = Config.languages
      if (segments.length > 1 && languages.includes(segments[1])) {
        segments[1] = language
      } else {
        segments.splice(1, 0, language)
      }
      urlObject.pathname = segments.join('/')
      let newUrl = urlObject.toString()
      if (newUrl.endsWith('/')) {
        newUrl = newUrl.slice(0, -1)
      }
      return newUrl
    }

    // Tracking parameters (utm_*, fbclid) and fragments must never end up in
    // canonical or alternate URLs, or every campaign link becomes a duplicate.
    const stripQuery = (url: string): string => {
      const urlObject = new URL(url)
      urlObject.search = ''
      urlObject.hash = ''
      return urlObject.toString()
    }

    const absolute = (url: string): string =>
      url.startsWith('/') ? Config.baseUrl.replace(/\/$/, '') + url : url

    // Open Graph wants territory-qualified locales. The site addresses Swiss
    // customers in German and writes its English in US English.
    const ogLocales: Record<string, string> = { de: 'de_CH', en: 'en_US' }

    // `<` is escaped so text inside the JSON can never close the script tag.
    const serializeJsonLd = (data: Record<string, unknown>): string =>
      JSON.stringify(data).replace(/</g, '\\u003c')

    const title = computed(() => props.metaDataFn().title?.value)
    const description = computed(() => props.metaDataFn().description?.value)
    const image = computed(() => props.metaDataFn().image?.value)
    const url = computed(() => {
      const value = props.metaDataFn().url?.value
      return value ? stripQuery(value) : undefined
    })
    const noIndex = computed(() => props.metaDataFn().noIndex?.value)
    const language = computed(() => props.metaDataFn().language?.value)
    const canonical = computed(() => {
      const override = props.metaDataFn().canonical?.value
      if (override) return override
      if (!url.value || !language.value) return undefined
      return setLanguageSegment(url.value, language.value)
    })
    const type = computed(() => props.metaDataFn().type?.value ?? 'website')
    const jsonLd = computed(() => props.metaDataFn().jsonLd?.value ?? [])

    return () => {

      const headers = []
      headers.push(createCommentVNode('meta-tags-start'))

      if (props.ssr || !import.meta.env.SSR) {
        headers.push(h('meta', { property: 'og:site_name', content: 'ZNIPP' }))
        headers.push(h('meta', { property: 'og:type', content: type.value }))

        if (title?.value) {
          headers.push(h('title', {}, title.value))
          headers.push(
            h('meta', { property: 'og:title', content: title.value })
          )
          headers.push(
            h('meta', { name: 'twitter:title', content: title.value })
          )
        }
        if (description?.value) {
          headers.push(
            h('meta', { name: 'description', content: description.value })
          )
          headers.push(
            h('meta', {
              property: 'og:description',
              content: description.value,
            })
          )
          headers.push(
            h('meta', {
              name: 'twitter:description',
              content: description.value,
            })
          )
        }
        headers.push(
          h('meta', {
            name: 'twitter:card',
            content: image?.value ? 'summary_large_image' : 'summary',
          })
        )
        if (image?.value) {
          headers.push(
            h('meta', { property: 'og:image', content: absolute(image.value) })
          )
          headers.push(
            h('meta', { name: 'twitter:image', content: absolute(image.value) })
          )
        }
        if (noIndex?.value) {
          headers.push(h('meta', { name: 'robots', content: 'noindex' }))
        }

        if (language?.value) {
          headers.push(
            h('meta', {
              'http-equiv': 'content-language',
              content: language.value,
            })
          )
          headers.push(
            h('meta', {
              property: 'og:locale',
              content: ogLocales[language.value] ?? language.value,
            })
          )
          for (const lang of Config.languages) {
            if (lang === language.value) continue
            headers.push(
              h('meta', {
                property: 'og:locale:alternate',
                content: ogLocales[lang] ?? lang,
              })
            )
          }
        }

        // A page that should not be indexed has no canonical version and no
        // language alternates worth pointing search engines to.
        if (url?.value && !noIndex?.value) {
          for (const lang of Config.languages) {
            const langUrl = setLanguageSegment(url.value, lang)
            headers.push(
              h('link', {
                rel: 'alternate',
                hreflang: lang,
                href: langUrl,
              })
            )
          }

          const defaultUrl = removeLanguageSegment(url.value)
          headers.push(
            h('link', {
              rel: 'alternate',
              hreflang: 'x-default',
              href: defaultUrl,
            })
          )
        }

        if (canonical.value && !noIndex?.value) {
          headers.push(h('link', { rel: 'canonical', href: canonical.value }))
          headers.push(
            h('meta', { property: 'og:url', content: canonical.value })
          )
        }

        for (const data of jsonLd.value) {
          headers.push(
            h('script', {
              type: 'application/ld+json',
              innerHTML: serializeJsonLd(data),
            })
          )
        }
      }

      headers.push(createCommentVNode('meta-tags-end'))
      return headers
    }
  },
})
